/**
 * Registering the service worker, and noticing when a new one is ready.
 *
 * Two things live here and nothing else does. The worker itself is `public/sw.js`, served from
 * the root so that its scope covers every route — a worker registered from `/assets/` would only
 * control `/assets/`, which is the classic way to ship a PWA that never works offline.
 *
 * The update path is the interesting half. A new build produces a new `sw.js`, which installs and
 * then *waits*, because taking over mid-session would swap the code under a page holding an open
 * map. So the application is told instead, and asks. Accepting reloads once the new worker has
 * taken control; declining changes nothing and the update applies on the next visit.
 */

/** Called when a new version has installed and is waiting for permission to take over. */
export type UpdateListener = () => void;

let waiting: ServiceWorker | null = null;
const listeners = new Set<UpdateListener>();

function announce(worker: ServiceWorker): void {
  waiting = worker;
  for (const listener of listeners) listener();
}

export function onUpdateReady(listener: UpdateListener): () => void {
  listeners.add(listener);
  // A worker that was already waiting when the listener arrived still counts as ready.
  if (waiting !== null) listener();
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Applies the waiting update.
 *
 * The reload is driven by `controllerchange` rather than called straight after the message: the
 * new worker has to be in control before the page asks for documents again, or the reload is
 * served by the worker being replaced and the update appears not to have happened.
 */
export function applyUpdate(): void {
  const worker = waiting;
  if (worker === null) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
  worker.postMessage('skip-waiting');
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // Not in development: a worker caching the dev server's modules turns every edit into a
  // mystery about which version of the file is on screen.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        if (registration.waiting !== null) announce(registration.waiting);

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (installing === null) return;
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install, and that install is not an update
            // — telling somebody their brand-new app has an update available is nonsense.
            if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
              announce(installing);
            }
          });
        });
      })
      .catch(() => {
        // A registration that fails leaves an application that works online and does not work
        // offline, which is exactly what it was before the worker existed. Nothing to report.
      });
  });
}
